import React, { useState, useEffect, useCallback } from "react";
import { 
  Table, Tag, Typography, Input, Select, Button, Row, Col, Card, 
  Spin, Empty, Space, message, DatePicker, Avatar, Dropdown, Menu,
  Tooltip, Badge
} from "antd";
import { 
  SearchOutlined,
  FilterOutlined,
  ReloadOutlined,
  EyeOutlined,
  MoreOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  CreditCardOutlined,
  BankOutlined,
  WalletOutlined
} from "@ant-design/icons";
import { fetchInvoices } from "../../ApiServices/getInvoices";

const { Option } = Select;
const { RangePicker } = DatePicker;
const { Text } = Typography;

const Payments = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    transactionId: "",
    customerName: "",
    status: null,
    dateRange: null,
  });

  const loadInvoices = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const invoicesData = await fetchInvoices();
      
      // Transform the data to ensure proper structure
      const transformedData = Array.isArray(invoicesData) 
        ? invoicesData.map(invoice => ({
            id: invoice.id || invoice.invoiceId || `INV-${Math.random().toString(36).substr(2, 9)}`,
            customer: {
              name: invoice.name || 'Unknown Customer',
            },
            date: invoice.date || invoice.createdAt || invoice.paymentDate || new Date().toISOString(),
            amount: invoice.amount || invoice.total || 0,
            currency: invoice.currency || 'USD',
            paymentMethod: invoice.paymentMethod || invoice.method || 'card',
            status: invoice.status || 'pending',
            description: invoice.description || invoice.note || 'Payment transaction'
          }))
        : [];
      
      setPayments(transformedData);
      message.success("Payments loaded successfully");
    } catch (error) {
      console.error("Error loading invoices:", error);
      setError(error.message || "Failed to load payments");
      message.error("Failed to load payments");
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    loadInvoices();
  }, [loadInvoices]);

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return dateString;
    }
  };

  // Apply filters
  const handleApplyFilters = () => {
    // Filtering is already handled by filteredPayments computed value
    message.info("Filters applied");
  };

  // Reset filters
  const handleResetFilters = () => {
    setFilters({
      transactionId: "",
      customerName: "",
      status: null,
      dateRange: null,
    });
    message.info("Filters cleared");
  };

  // Filter payments with safe property access
  const filteredPayments = payments.filter(payment => {
    if (!payment) return false;
    
    if (filters.transactionId) {
      const transactionId = payment.id || '';
      if (!transactionId.toString().toLowerCase().includes(filters.transactionId.toLowerCase())) {
        return false;
      }
    }
    
    if (filters.customerName) {
      const customerName = payment.customer?.name || '';
      if (!customerName.toLowerCase().includes(filters.customerName.toLowerCase())) {
        return false;
      }
    }
    
    if (filters.status) {
      const paymentStatus = payment.status || '';
      if (paymentStatus.toLowerCase() !== filters.status.toLowerCase()) {
        return false;
      }
    }
    
    // Date range filter (if implemented)
    if (filters.dateRange && payment.date) {
      const [start, end] = filters.dateRange;
      const paymentDate = new Date(payment.date);
      if (paymentDate < start || paymentDate > end) {
        return false;
      }
    }
    
    return true;
  });

  // Get status config
  const getStatusConfig = (status) => {
    if (!status) return { color: "default", icon: <ClockCircleOutlined />, text: "Unknown" };
    
    const statusLower = status.toLowerCase();
    switch(statusLower) {
      case "completed":
      case "success":
      case "paid":
      case "successful":
        return { color: "green", icon: <CheckCircleOutlined />, text: "Completed" };
      case "pending":
      case "processing":
        return { color: "orange", icon: <ClockCircleOutlined />, text: "Pending" };
      case "failed":
      case "canceled":
      case "cancelled":
      case "rejected":
        return { color: "red", icon: <CloseCircleOutlined />, text: "Failed" };
      default:
        return { color: "default", icon: <ClockCircleOutlined />, text: status };
    }
  };

  // Get payment method icon
  const getPaymentMethodIcon = (method) => {
    if (!method) return <CreditCardOutlined />;
    
    const methodLower = method.toLowerCase();
    switch(methodLower) {
      case "card": 
      case "credit_card":
      case "credit card":
        return <CreditCardOutlined />;
      case "paypal": 
        return <BankOutlined />;
      case "bank_transfer": 
      case "bank transfer":
      case "bank":
        return <BankOutlined />;
      case "wallet": 
      case "digital_wallet":
        return <WalletOutlined />;
      default: 
        return <CreditCardOutlined />;
    }
  };

  // Format currency
  const formatCurrency = (amount, currency) => {
    const safeAmount = parseFloat(amount) || 0;
    const safeCurrency = currency || 'USD';
    
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: safeCurrency
    }).format(safeAmount);
  };

  // Table columns
  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 100,
      render: (id) => (
        <Text style={{ fontFamily: 'monospace', fontSize: '12px' }}>
          {id ? id.toString().substring(0, 8) + '...' : 'N/A'}
        </Text>
      )
    },
    {
      title: "Customer",
      dataIndex: "customer",
      key: "customer",
      width: 180,
      render: (customer) => {
        // Safe rendering for customer
        const safeCustomer = customer || {};
        return (
          <div>
            <div style={{ fontWeight: 500 }}>
              {safeCustomer.name || 'Unknown Customer'}
            </div>
          </div>
        );
      }
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      width: 150,
      sorter: (a, b) => {
        const dateA = a.date ? new Date(a.date) : new Date(0);
        const dateB = b.date ? new Date(b.date) : new Date(0);
        return dateA - dateB;
      },
      render: (date) => formatDate(date)
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      width: 120,
      sorter: (a, b) => (a.amount || 0) - (b.amount || 0),
      render: (amount, record) => {
        const safeAmount = parseFloat(amount) || 0;
        return (
          <Text strong style={{ color: safeAmount > 0 ? '#52c41a' : '#f5222d' }}>
            {formatCurrency(safeAmount, record?.currency)}
          </Text>
        );
      }
    },
    {
      title: "Method",
      dataIndex: "paymentMethod",
      key: "paymentMethod",
      width: 100,
      render: (method) => {
        const safeMethod = method || 'card';
        return (
          <Tooltip title={safeMethod.toUpperCase()}>
            <span style={{ fontSize: '16px' }}>
              {getPaymentMethodIcon(safeMethod)}
            </span>
          </Tooltip>
        );
      }
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (status) => {
        const config = getStatusConfig(status);
        return (
          <Tag color={config.color} icon={config.icon}>
            {config.text}
          </Tag>
        );
      }
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      ellipsis: true,
      render: (description) => description || 'No description'
    },
    {
      title: "Actions",
      key: "actions",
      width: 80,
      render: (_, record) => (
        <Dropdown
          overlay={
            <Menu>
              <Menu.Item 
                icon={<EyeOutlined />}
                onClick={() => {
                  console.log("View details for:", record);
                  message.info(`Viewing details for ${record.id}`);
                }}
              >
                View Details
              </Menu.Item>
              <Menu.Item 
                onClick={() => {
                  navigator.clipboard.writeText(record.id);
                  message.success("ID copied to clipboard");
                }}
              >
                Copy ID
              </Menu.Item>
            </Menu>
          }
          trigger={['click']}
        >
          <Button type="text" icon={<MoreOutlined />} size="small" />
        </Dropdown>
      )
    }
  ];

  return (
    <Card
      title={
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span>Payment Transactions</span>
          <div>
            {error && (
              <Badge 
                status="error" 
                text={<Text type="danger" style={{ marginRight: 8 }}>{error}</Text>} 
              />
            )}
            <Button 
              icon={<ReloadOutlined />} 
              onClick={loadInvoices} 
              loading={loading}
              size="small"
            >
              Refresh
            </Button>
          </div>
        </div>
      }
    >
      {/* Filters */}
      <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
        <Col xs={24} sm={12} md={6}>
          <Input
            placeholder="Transaction ID"
            value={filters.transactionId}
            onChange={(e) => setFilters({...filters, transactionId: e.target.value})}
            prefix={<SearchOutlined />}
            allowClear
          />
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Input
            placeholder="Customer Name"
            value={filters.customerName}
            onChange={(e) => setFilters({...filters, customerName: e.target.value})}
            prefix={<SearchOutlined />}
            allowClear
          />
        </Col>
        <Col xs={24} sm={12} md={4}>
          <Select
            placeholder="Status"
            style={{ width: '100%' }}
            value={filters.status}
            onChange={(value) => setFilters({...filters, status: value})}
            allowClear
          >
            <Option value="completed">Completed</Option>
            <Option value="pending">Pending</Option>
            <Option value="failed">Failed</Option>
          </Select>
        </Col>
        <Col xs={24} sm={12} md={4}>
          <RangePicker
            style={{ width: '100%' }}
            onChange={(dates) => setFilters({...filters, dateRange: dates})}
            value={filters.dateRange}
          />
        </Col>
        <Col xs={24} sm={12} md={4}>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button 
              icon={<FilterOutlined />}
              onClick={handleApplyFilters}
              type="primary"
              style={{ flex: 1 }}
            >
              Filter
            </Button>
            <Button 
              onClick={handleResetFilters}
              style={{ flex: 1 }}
            >
              Clear
            </Button>
          </div>
        </Col>
      </Row>

      {/* Stats Summary */}
      <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
        <Col xs={24} sm={8}>
          <Card size="small">
            <StatCard 
              title="Total Transactions" 
              value={filteredPayments.length} 
              color="#1890ff" 
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card size="small">
            <StatCard 
              title="Total Amount" 
              value={formatCurrency(
                filteredPayments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0)
              )} 
              color="#52c41a" 
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card size="small">
            <StatCard 
              title="Completed" 
              value={filteredPayments.filter(p => p.status?.toLowerCase() === 'completed').length} 
              color="#52c41a" 
            />
          </Card>
        </Col>
      </Row>

      {/* Table */}
      {loading ? (
        <div style={{ textAlign: "center", padding: 50 }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>Loading payments...</div>
        </div>
      ) : filteredPayments.length === 0 ? (
        <Empty 
          description={
            <div>
              <div>No payment transactions found</div>
              <Text type="secondary">
                {payments.length > 0 ? 'Try changing your filters' : 'No data available'}
              </Text>
            </div>
          }
        >
          <Button type="primary" onClick={loadInvoices}>
            Reload Data
          </Button>
        </Empty>
      ) : (
        <Table
          columns={columns}
          dataSource={filteredPayments.map(item => ({ ...item, key: item.id }))}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `Total ${total} transactions`
          }}
          scroll={{ x: 1000 }}
          size="middle"
          rowKey="id"
        />
      )}
    </Card>
  );
};

// Helper component for stats cards
const StatCard = ({ title, value, color }) => (
  <div style={{ textAlign: 'center' }}>
    <div style={{ fontSize: '12px', color: '#666', marginBottom: 4 }}>{title}</div>
    <div style={{ fontSize: '24px', fontWeight: 'bold', color }}>{value}</div>
  </div>
);

export default Payments;